// Pipeline Copilot — pure helpers behind the Explain panel family and the
// Morning Brief page (port of the approved Claude Design bundle,
// pipeline-copilot/*). Everything here is framework-free and unit-tested in
// copilot.test.ts; components/copilot.tsx renders on top of these.
//
// The load-bearing rule (design brief §3): the UI never renders a claim whose
// refs don't all resolve — `resolvableClaims` is that guard.

import type {
  EvidenceItem,
  ExplainClaim,
  ExplainKind,
  ExplainRequest,
  ExplainResponse,
  HistoryBucket,
  StageAttribution,
  Visual,
} from '@/api/types.gen';
import { fmtHM, fmtDayShort } from '@/lib/datetime';

// ── ref resolution / claim filtering ────────────────────────────────────────

/**
 * Resolve a claim's refs against the response's evidence list. Returns the
 * cited items in ref order, or null when ANY ref is missing — a claim with a
 * dangling citation must not render at all.
 */
export function resolveRefs(
  claim: ExplainClaim,
  evidence: ReadonlyArray<EvidenceItem>,
): EvidenceItem[] | null {
  const out: EvidenceItem[] = [];
  for (const id of claim.refs) {
    const ev = evidence.find((e) => e.id === id);
    if (!ev) return null;
    out.push(ev);
  }
  return out;
}

/** Claims whose refs all resolve, in display order (the render guard). */
export function resolvableClaims(
  claims: ReadonlyArray<ExplainClaim>,
  evidence: ReadonlyArray<EvidenceItem>,
): ExplainClaim[] {
  return claims.filter((c) => resolveRefs(c, evidence) !== null);
}

// ── trace linkage: refs `lN` ↔ rendered line numbers ────────────────────────

/**
 * Line number cited by a trace evidence item, parsed from its label
 * ("line 9" → 9, per the wire contract). Null when the label carries none.
 */
export function traceLineNo(ev: EvidenceItem): number | null {
  const m = /(?:^|\b)line\s+(\d+)/i.exec(ev.label);
  return m ? parseInt(m[1]!, 10) : null;
}

/** Set of line numbers a claim cites (unresolvable/lineless refs skipped). */
export function citedLineNumbers(
  claim: ExplainClaim,
  evidence: ReadonlyArray<EvidenceItem>,
): ReadonlySet<number> {
  const out = new Set<number>();
  for (const id of claim.refs) {
    const ev = evidence.find((e) => e.id === id);
    const n = ev ? traceLineNo(ev) : null;
    if (n != null) out.add(n);
  }
  return out;
}

/**
 * /admin/logs page cap (the API's DEFAULT_LOGS_LIMIT): the stream fetch
 * returns the NEWEST 100 lines. The server numbers its trace refs `lN` over
 * the trace's FIRST 50 chronological lines (batch-worker explain_trace
 * MAX_LINES) — so once a trace fills the whole page, the oldest lines may be
 * missing client-side and positional numbering can no longer be trusted.
 */
export const TRACE_LINE_FETCH_CAP = 100;

/**
 * True when the rendered trace list may be missing its oldest lines (fetch
 * cap hit) — positional `line N` citations must degrade to static chips
 * (no scroll / no highlight) because they may point at the wrong rows.
 */
export function traceCiteDegraded(lineCount: number): boolean {
  return lineCount >= TRACE_LINE_FETCH_CAP;
}

/** Title carried by the inert citation chips when the cap guard trips. */
export const TRACE_CITE_CAP_NOTE = 'line numbering unavailable for very long traces';

/**
 * The model's DISPLAY name — every user-facing label ("AI-GENERATED · RA",
 * "Asking RA…", "RA UNAVAILABLE") interpolates this one constant. It is a
 * product name, deliberately decoupled from the wire `model` id
 * (`gemma-4-31B`), which is data and is never rendered raw.
 */
export const CP_MODEL_LABEL = 'RA';

// ── evidence-chip dispatch: navigate vs scroll/highlight ────────────────────

export type ChipAction = 'link' | 'scroll';

/** `link` chips navigate the app route; `link: null` chips scroll in place. */
export function chipAction(ev: EvidenceItem): ChipAction {
  return ev.link == null ? 'scroll' : 'link';
}

// ── formatting ──────────────────────────────────────────────────────────────

/** Footer clock — the generated_at instant as the NY wall clock HH:MM (design cpClock). */
export function clockLabel(iso: string): string {
  return fmtHM(iso);
}

/** Age label for `CACHED · <age>` ("just now" / "12m ago" / "6h ago" / "3d ago"). */
export function formatAge(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const mins = Math.floor(Math.max(0, nowMs - t) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Brief day "2026-07-11" → "JUL 11" (calendar day — literal, never tz-shifted). */
export function briefDayLabel(day: string): string {
  return fmtDayShort(day).toUpperCase();
}

/** A brief older than this renders the stale variant. */
export const BRIEF_STALE_MS = 36 * 60 * 60 * 1000;

export function isBriefStale(generatedAtIso: string, nowMs: number): boolean {
  const t = Date.parse(generatedAtIso);
  if (!Number.isFinite(t)) return false;
  return nowMs - t > BRIEF_STALE_MS;
}

// ── loading skeleton — reserved heights per kind (design CP_SKEL_H) ─────────

export type SkeletonKind = ExplainKind | 'brief';

export const CP_SKEL_H: Record<SkeletonKind, number> = {
  chart_point: 168,
  alert: 158,
  trace: 198,
  ingest_run: 172,
  production: 240,
  brief: 320,
};

export function skeletonHeight(kind: SkeletonKind): number {
  return CP_SKEL_H[kind] ?? 160;
}

// ── ranked claims / next-step verdicts ──────────────────────────────────────

/** Split a ranked-cause claim ("1. The heartbeat…") into rank + prose. */
export function parseRank(text: string): { rank: string | null; text: string } {
  const m = /^(\d+)\.\s*([\s\S]*)$/.exec(text);
  return m ? { rank: m[1]!, text: m[2]! } : { rank: null, text };
}

/** Split a next-step "PILL — rest of the sentence" on the first em-dash. */
export function splitNextStep(text: string): { pill: string; rest: string } {
  const parts = text.split(' — ');
  return { pill: parts[0] ?? text, rest: parts.slice(1).join(' — ') };
}

export type VerdictTone = 'good' | 'warn' | 'bad';

/** Deterministic verdict → house tone (SAFE / INVESTIGATE / everything else). */
export function verdictTone(text: string): VerdictTone {
  const t = text.toUpperCase();
  if (t.startsWith('SAFE')) return 'good';
  if (t.startsWith('INVESTIGATE')) return 'bad';
  return 'warn';
}

// ── chart-explain dock reset identity ───────────────────────────────────────

/**
 * Identity of a HISTORY chart scope — range bounds, granularity and the
 * production/pixel filters. The chart-explain dock closes when THIS changes
 * (a genuine re-scope repaints the buckets under the docked answer) and on
 * nothing else: the pipeline page re-renders every 5s (clock tick + snapshot
 * polls), and derived-object identity churn must never close an open dock
 * mid-generation.
 */
export function chartDockResetKey(q: {
  from: string;
  to: string;
  granularity: string;
  productionId?: string | null;
  pixelId?: string | null;
}): string {
  return [q.from, q.to, q.granularity, q.productionId ?? '', q.pixelId ?? ''].join('|');
}

// ── explain-request builders (one per anchor) ───────────────────────────────

export interface ChartExplainArgs {
  /** History lane the chart draws from: 'web_events' | 'tickets'. */
  lane: string;
  /** Series key within the lane (e.g. 'total', 'person_ct'). */
  metric: string;
  /** Bucket start instant (the enumerated axis value the user clicked). */
  bucket: string;
  granularity: HistoryBucket;
  productionId: string | null;
}

export function chartExplainRequest(args: ChartExplainArgs): ExplainRequest {
  return {
    params: {
      kind: 'chart_point',
      lane: args.lane,
      metric: args.metric,
      bucket: args.bucket,
      granularity: args.granularity,
      production_id: args.productionId,
    },
  };
}

export function alertExplainRequest(alertId: string): ExplainRequest {
  return { params: { kind: 'alert', alert_id: alertId } };
}

export function traceExplainRequest(traceId: string): ExplainRequest {
  return { params: { kind: 'trace', trace_id: traceId } };
}

export function runExplainRequest(runId: string): ExplainRequest {
  return { params: { kind: 'ingest_run', run_id: runId } };
}

export function productionExplainRequest(productionId: string): ExplainRequest {
  return { params: { kind: 'production', production_id: productionId } };
}

// ── server-built visuals + stage verdict (rendered by copilot-visuals.tsx) ──

/** Which existing SVG chart renders a visual spec. */
export type VisualComponentKind = 'metric' | 'coverage' | 'sparkline';

export function visualComponent(v: Visual): VisualComponentKind {
  switch (v.kind) {
    case 'coverage':
      return 'coverage';
    case 'line':
      return 'sparkline';
    case 'bars':
    default:
      return 'metric';
  }
}

/** Axis/tip/peak formatter for a visual's unit (append `%` for percent lanes). */
export function visualFmt(unit: string | null): (v: number) => string {
  const suffix = unit === '%' ? '%' : '';
  return (v: number) => {
    const r = Math.round(v * 10) / 10;
    return (Number.isInteger(r) ? String(r) : r.toFixed(1)) + suffix;
  };
}

/** Product-stage display labels for the highlighted diagram. */
export const STAGE_LABEL: Record<string, string> = {
  ingest: 'Ingest',
  enrich: 'Enrich',
  enqueue: 'Enqueue',
  meta: 'Meta CAPI',
  ttd: 'UID2 → TTD',
};

/** Diagram node class: band coloring + the most-implicated emphasis. */
export function stageNodeClass(s: StageAttribution): string {
  return 'cp-stage-node band-' + s.band + (s.most_implicated ? ' implicated' : '');
}

/**
 * True when the response carries SERVER-BUILT artifacts (the stage verdict
 * and/or charts). These are trustworthy without the model, so the degraded
 * branch still renders them — and the footer labels their provenance.
 */
export function hasServerVisuals(r: Pick<ExplainResponse, 'verdict' | 'visuals'>): boolean {
  return r.verdict != null || (r.visuals?.length ?? 0) > 0;
}
